import { useState, useEffect } from "react";
import { MessageSquareShare, ShieldCheck, ArrowDown } from "lucide-react";
import { toast } from "sonner";
import { MigrationForm } from "@/components/migration/MigrationForm";
import { ResponsePanel } from "@/components/migration/ResponsePanel";
import { HistoryPanel } from "@/components/migration/HistoryPanel";
import {
  migrateTemplates,
  validateMigrationInput,
  isSuccessfulMigration,
  checkHealth,
  preflightAccess,
} from "@/lib/api";

const HISTORY_KEY = "wa_migration_history";

const loadHistory = () => {
  try {
    return JSON.parse(sessionStorage.getItem(HISTORY_KEY)) || [];
  } catch {
    return [];
  }
};

export default function MigrationTool() {
  const [values, setValues] = useState({
    sourceWabaId: "",
    destinationWabaId: "",
    accessToken: "",
    apiVersion: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState(loadHistory);
  const [backendStatus, setBackendStatus] = useState("checking");
  const [verifying, setVerifying] = useState(false);
  const [preflight, setPreflight] = useState(null);

  useEffect(() => {
    sessionStorage.setItem(HISTORY_KEY, JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    let active = true;
    const ping = async () => {
      try {
        await checkHealth();
        if (active) setBackendStatus("online");
      } catch {
        if (active) setBackendStatus("offline");
      }
    };
    ping();
    const id = setInterval(ping, 30000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const handleChange = (field, value) => {
    setValues((v) => ({ ...v, [field]: value }));
    setErrors((e) => ({ ...e, [field]: undefined }));
    setPreflight(null);
  };

  const handleVerify = async () => {
    const errs = {};
    const s = values.sourceWabaId.trim();
    const d = values.destinationWabaId.trim();
    const t = values.accessToken.trim();
    if (!s) errs.sourceWabaId = "Source WABA ID is required";
    else if (!/^\d+$/.test(s)) errs.sourceWabaId = "Must contain only numeric characters";
    if (!d) errs.destinationWabaId = "Destination WABA ID is required";
    else if (!/^\d+$/.test(d)) errs.destinationWabaId = "Must contain only numeric characters";
    if (!t) errs.accessToken = "Access Token is required";
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please fix the highlighted fields");
      return;
    }
    setErrors({});
    setVerifying(true);
    setPreflight(null);
    try {
      const data = await preflightAccess(values);
      if (data.validation_errors) {
        toast.error(data.validation_errors[0]);
        return;
      }
      setPreflight(data);
      if (data.source.reachable && data.destination.reachable) {
        toast.success("Both WABAs are reachable with this token");
      } else {
        toast.error("One or both WABAs are not reachable — check the token's access");
      }
    } catch (err) {
      toast.error("Verification request failed. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async () => {
    const validation = validateMigrationInput(values);
    if (Object.keys(validation).length) {
      setErrors(validation);
      toast.error("Please fix the highlighted fields");
      return;
    }
    setErrors({});
    setLoading(true);
    setResult(null);
    try {
      const data = await migrateTemplates(values);

      if (data.validation_errors) {
        toast.error(data.validation_errors[0]);
        setLoading(false);
        return;
      }

      setResult(data);

      const success = isSuccessfulMigration(data);
      // History entry — never contains the access token
      const entry = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
        source_waba_id: data.source_waba_id,
        destination_waba_id: data.destination_waba_id,
        request_timestamp: data.request_timestamp,
        http_status: data.http_status,
        success,
        response_body: data.response_body,
      };
      setHistory((h) => [entry, ...h]);

      if (success) toast.success("Meta accepted the migration request");
      else toast.error(`Meta returned HTTP ${data.http_status}`);
    } catch (err) {
      // Surface the ACTUAL backend/Meta error in the Migration Status section
      // instead of a generic message.
      const errResult = {
        http_status: err?.response?.status || 0,
        response_headers: {},
        response_body:
          err?.response?.data || {
            error: {
              message:
                err?.message ||
                "Could not reach the migration backend. Verify the backend is running and /api/migrate is reachable.",
              type: "client_request_error",
            },
          },
        request_url: "",
        request_timestamp: new Date().toISOString(),
        fbtrace_id: null,
        source_waba_id: (values.sourceWabaId || "").trim(),
        destination_waba_id: (values.destinationWabaId || "").trim(),
      };
      setResult(errResult);
      setHistory((h) => [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          source_waba_id: errResult.source_waba_id,
          destination_waba_id: errResult.destination_waba_id,
          request_timestamp: errResult.request_timestamp,
          http_status: errResult.http_status,
          success: false,
          response_body: errResult.response_body,
        },
        ...h,
      ]);
      toast.error(
        errResult.response_body?.error?.message || "Request failed. See Migration Status for details."
      );
    } finally {
      setLoading(false);
      // Clear the token from client memory after the request
      setValues((v) => ({ ...v, accessToken: "" }));
    }
  };

  const clearHistory = () => {
    setHistory([]);
    sessionStorage.removeItem(HISTORY_KEY);
    toast.success("History cleared");
  };

  return (
    <div className="App min-h-screen bg-zinc-950 text-zinc-100">
      {/* Header */}
      <header className="border-b border-zinc-800/80 bg-zinc-950">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-gradient-to-br from-blue-600 to-emerald-500 p-2.5">
              <MessageSquareShare className="h-5 w-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg sm:text-xl font-bold leading-tight tracking-tight text-zinc-50">
                WhatsApp Template Migration Tool
              </h1>
              <p className="text-xs text-zinc-400 font-mono">Official Meta Cloud API Migration</p>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-4 text-xs font-mono">
            <span
              data-testid="backend-status-badge"
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 ${
                backendStatus === "online"
                  ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-400"
                  : backendStatus === "offline"
                  ? "border-red-500/20 bg-red-500/10 text-red-400"
                  : "border-zinc-700 bg-zinc-900 text-zinc-400"
              }`}
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${
                  backendStatus === "online"
                    ? "bg-emerald-400"
                    : backendStatus === "offline"
                    ? "bg-red-400"
                    : "bg-zinc-500 animate-pulse"
                }`}
              />
              {backendStatus === "online"
                ? "Backend connected"
                : backendStatus === "offline"
                ? "Backend unreachable"
                : "Checking backend..."}
            </span>
            <span className="inline-flex items-center gap-1.5 text-zinc-500">
              <ShieldCheck className="h-4 w-4 text-emerald-500" />
              Token never stored
            </span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Left — configuration form */}
          <div className="lg:col-span-4">
            <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-6 lg:sticky lg:top-8">
              <div className="mb-5">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300">Migration Configuration</h2>
                <p className="mt-1 text-xs text-zinc-500 leading-relaxed">
                  Templates are migrated from the source to the destination WABA using Meta's native process.
                </p>
              </div>
              <MigrationForm
                values={values}
                errors={errors}
                onChange={handleChange}
                onSubmit={handleSubmit}
                loading={loading}
                onVerify={handleVerify}
                verifying={verifying}
                preflight={preflight}
              />
            </div>
          </div>

          {/* Right — status, response, history */}
          <div className="lg:col-span-8 space-y-8">
            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300 mb-4">Migration Status</h2>

              {loading && (
                <div data-testid="status-loading" className="flex flex-col items-center justify-center py-14 gap-3">
                  <div className="h-10 w-10 rounded-full border-2 border-zinc-700 border-t-emerald-500 animate-spin" />
                  <p className="text-sm text-zinc-300 font-medium">Migrating templates...</p>
                  <p className="text-xs text-zinc-500">Please wait for Meta's response.</p>
                </div>
              )}

              {!loading && !result && (
                <div data-testid="status-idle" className="flex flex-col items-center justify-center py-14 gap-2 text-center">
                  <ArrowDown className="h-6 w-6 text-zinc-600" />
                  <p className="text-sm text-zinc-400">Enter your WABA details and run a migration.</p>
                  <p className="text-xs text-zinc-600">The exact Meta API response will appear below.</p>
                </div>
              )}

              {!loading && result && (
                <div className="space-y-6">
                  <div className="border-b border-zinc-800 pb-1">
                    <h3 className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Exact Meta API Response</h3>
                  </div>
                  <ResponsePanel result={result} />
                </div>
              )}
            </section>

            <section className="rounded-lg border border-zinc-800 bg-zinc-900 p-6">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-zinc-300 mb-4">Migration History</h2>
              <HistoryPanel history={history} onClear={clearHistory} onSelect={(item) => {}} />
            </section>
          </div>
        </div>
      </main>

      <footer className="border-t border-zinc-800/80 py-6">
        <div className="mx-auto max-w-7xl px-4 text-center text-xs text-zinc-600 font-mono">
          Uses Meta Graph API · <span className="text-zinc-500">migrate_message_templates</span> · Responses shown exactly as returned by Meta
        </div>
      </footer>
    </div>
  );
}
